import type { CareGrade } from './common'

/** 감경 유형 */
export type ReductionType =
  | 'NONE'              // 일반 (감경 없음)
  | 'GENERAL'           // 일반 (호환용)
  | 'BASIC_LIVELIHOOD'  // 면제 — 기초생활수급자(의료급여 수급)
  | 'NEAR_POVERTY_1'    // 60% 감경 (enum 이름은 백엔드 계약이라 유지)
  | 'NEAR_POVERTY_2'    // 40% 감경 (enum 이름은 백엔드 계약이라 유지)

/** 시설 규모 / 방문시간 코드 */
export type FacilitySize =
  | 'LARGE' | 'SMALL'   // 요양원: 10인 이상 / 10인 미만
  | 'DEFAULT'            // 주간보호: 규모 구분 없음
  | '30MIN' | '60MIN' | '90MIN' | '120MIN' | '150MIN' | '180MIN' | '210MIN' | '240MIN'  // 방문요양: 1회 방문시간

/** 비용 시뮬레이션 요청 */
export interface CostSimulationRequest {
  careGrade: CareGrade
  facilityType: 'NURSING_HOME' | 'DAY_CARE' | 'HOME_VISIT_CARE'
  daysPerMonth: number
  reductionType: ReductionType
  facilitySize: FacilitySize
  includeNonCovered: boolean
  region?: string
  /** 시설 ID (있으면 해당 시설의 실제 비급여 데이터 사용) */
  facilityId?: number
}

/** 비급여 항목 */
export interface NonCoveredItem {
  name: string
  monthlyAverage: number
  description?: string
}

/** 비급여 추정치 */
export interface NonCoveredEstimate {
  min: number
  max: number
  items: NonCoveredItem[]
}

/** 총 예상 비용 */
export interface TotalEstimate {
  min: number
  max: number
}

/** 비용 시뮬레이션 응답 (실제 API 구조) */
export interface CostSimulationResponse {
  copayAmount: number
  totalBenefit: number
  insuranceCoverage: number
  copayRate: number
  dailyRate: number
  daysPerMonth: number
  reductionType: string
  /** 백엔드는 includeNonCovered=false 또는 비급여 데이터 부재 시 null 을 반환한다. */
  nonCoveredEstimate: NonCoveredEstimate | null
  totalEstimate: TotalEstimate
  monthlyLimit: number | null
  limitExcessAmount: number
  /**
   * 1·2등급 방문요양 중증 수급자 가산 월 합계(원). 전액 공단 부담이라 copayAmount 에 없다.
   * 해당 없으면 0. 구 응답 호환으로 옵셔널 (지침 §3.3.1·§4.2)
   */
  severeGradeSurcharge?: number
  /**
   * 요양원(10인 이상)일 때 요양보호사 배치 2.1:1 미만 시설 기준 참고 금액(같은 감경 유형).
   * 그 외 null. 구 응답 호환으로 옵셔널 (지침 §3.1.1·§4.2)
   */
  understaffedRate?: UnderstaffedRate | null
}

/** 요양보호사 배치 2.1:1 미만 시설 기준 참고 금액 */
export interface UnderstaffedRate {
  dailyRate: number
  totalBenefit: number
  copayAmount: number
}

/** 수가 테이블 항목 (실제 API 구조) */
export interface CareRateEntry {
  careGrade: string
  facilityType: string
  facilitySize: string
  dailyRate: number
  copayRateNormal: number
  copayRateBasic: number
  copayRateNear1: number
  copayRateNear2: number
  monthlyBenefit30Days: number
}

/** 수가 테이블 응답 - 실제 API는 배열로 반환 (data: CareRateEntry[]) */
export type CareRatesResponse = CareRateEntry[]

/*
 * 감경 표시 기준 — 영구지침: docs/develop/domain/cost/비용_시뮬레이터_구현_지침.md §7
 * (공식 기준 확인 2026-10-14).
 *
 * "차상위 1 / 차상위 2" 는 공식 용어가 아니라서 사용자가 자기 해당 여부를 판단할 수 없었다.
 * 라벨은 감경 정도, 보조 문구는 대상자로 적는다. enum 값은 백엔드 계약이라 그대로 둔다.
 */

/** 급여 종류 — 본인부담률이 갈린다. FACILITY=요양원(시설급여), HOME=주간보호·방문요양(재가급여) */
export type BenefitKind = 'FACILITY' | 'HOME'

/** 시뮬레이터 시설 유형 → 급여 종류 */
export function benefitKindOf(facilityType: CostSimulationRequest['facilityType'] | undefined): BenefitKind {
  return facilityType === 'NURSING_HOME' ? 'FACILITY' : 'HOME'
}

/**
 * 감경 유형별 본인부담률(%). 금액이 아니라 제도가 바뀌어야 움직이는 비율이라 상수로 둔다.
 * 백엔드 care_rates 의 copay_rate_* 와 같은 값이다(V88).
 */
export const COPAY_RATE_PERCENT: Record<BenefitKind, Record<ReductionType, number>> = {
  FACILITY: { NONE: 20, GENERAL: 20, BASIC_LIVELIHOOD: 0, NEAR_POVERTY_1: 8, NEAR_POVERTY_2: 12 },
  HOME: { NONE: 15, GENERAL: 15, BASIC_LIVELIHOOD: 0, NEAR_POVERTY_1: 6, NEAR_POVERTY_2: 9 },
}

/** 감경 유형 한글 라벨 */
export const REDUCTION_TYPE_LABELS: Record<ReductionType, string> = {
  NONE: '일반',
  GENERAL: '일반',
  BASIC_LIVELIHOOD: '본인부담 면제',
  NEAR_POVERTY_1: '60% 감경',
  NEAR_POVERTY_2: '40% 감경',
}

/** 감경 유형 대상자 — 선택 버튼 아래 보조 문구 */
export const REDUCTION_TYPE_HINTS: Record<ReductionType, string> = {
  NONE: '아래 어디에도 해당하지 않아요',
  GENERAL: '아래 어디에도 해당하지 않아요',
  BASIC_LIVELIHOOD: '기초생활수급자(의료급여 수급)',
  NEAR_POVERTY_1: '기초수급 아닌 의료급여·차상위 경감·보험료 하위 25%',
  NEAR_POVERTY_2: '건강보험료 하위 25~50%',
}

/** 감경 대상 공통 안내 */
export const REDUCTION_NOTE =
  '감경은 대부분 따로 신청하지 않아도 공단이 적용해요. 직장가입자는 보험료와 함께 재산 기준도 봐요.'

const BENEFIT_KIND_NAME: Record<BenefitKind, { name: string; topic: string }> = {
  FACILITY: { name: '요양원', topic: '요양원은' },
  HOME: { name: '방문요양·주간보호', topic: '방문요양·주간보호는' },
}

/** 감경 유형별 설명 — 선택한 시설 유형(급여 종류)의 비율로 만든다 */
export function getReductionDescription(type: ReductionType, kind: BenefitKind): string {
  const rates = COPAY_RATE_PERCENT[kind]
  const base = rates.NONE
  const { name, topic } = BENEFIT_KIND_NAME[kind]
  const nonCovered = '식재료비 같은 비급여는 감경 없이 전액 본인이 부담해요.'
  switch (type) {
    case 'BASIC_LIVELIHOOD':
      return `의료급여를 받는 기초생활수급자는 장기요양 본인부담금이 없어요(0%). ${nonCovered}`
    case 'NEAR_POVERTY_1':
      return `${name} 본인부담률이 ${base}%에서 ${rates.NEAR_POVERTY_1}%로 낮아져요(60% 감경). ${nonCovered}`
    case 'NEAR_POVERTY_2':
      return `${name} 본인부담률이 ${base}%에서 ${rates.NEAR_POVERTY_2}%로 낮아져요(40% 감경). ${nonCovered}`
    case 'NONE':
    case 'GENERAL':
      return `${topic} 급여비용의 ${base}%를 본인이 부담해요.`
  }
}
